import { buildSelectionQuestionPrompt, streamChat } from "../ai/client";
import { getAnnotation, getAnnotationByText, saveAnnotation } from "../db";
import type { EnglishLevel, SelectionAnnotation } from "../types";
import { setMarkdownContent } from "./markdown";
import { esc, toast } from "./toast";

const DEFAULT_ALLOWED = [
  ".diagnosis-source-body",
  ".markdown",
  ".summary-text",
  ".block-note",
  ".wizard-content",
  ".wizard-check",
];

type ReadingContextSource = string | (() => string);

type PanelSource = "cache" | "saved" | "stream";

export type AttachSelectionAskOptions = {
  root: HTMLElement;
  readingContext: ReadingContextSource;
  effectiveLevel: EnglishLevel;
  readingId?: string | null;
  allowedSelectors?: string[];
  onAnnotationSaved?: () => void;
};

function resolveContext(source: ReadingContextSource): string {
  return typeof source === "function" ? source() : source;
}

function isWhitespaceOnly(text: string): boolean {
  return !text.replace(/\s/g, "");
}

function selectionInAllowed(
  node: Node,
  root: HTMLElement,
  allowed: string[],
): boolean {
  const element =
    node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  if (!element || !root.contains(element)) {
    return false;
  }
  return allowed.some((selector) => element.closest(selector) !== null);
}

function positionFloating(el: HTMLElement, rect: DOMRect): void {
  const margin = 8;
  const btnW = el.offsetWidth || 72;
  const btnH = el.offsetHeight || 36;
  const halfW = btnW / 2;
  const centerX = rect.left + rect.width / 2;
  const clampedX = Math.min(
    Math.max(centerX, margin + halfW),
    window.innerWidth - margin - halfW,
  );

  let top = rect.top - margin;
  let transform = "translate(-50%, -100%)";
  if (top - btnH < margin) {
    top = rect.bottom + margin;
    transform = "translate(-50%, 0)";
  }
  if (transform === "translate(-50%, 0)" && top + btnH > window.innerHeight - margin) {
    top = Math.max(margin, window.innerHeight - margin - btnH);
  }

  el.style.left = `${clampedX}px`;
  el.style.top = `${top}px`;
  el.style.transform = transform;
}

function sourceBadge(source: PanelSource): string {
  if (source === "cache") {
    return `<span class="selection-ask-badge selection-ask-badge-cache">来自缓存</span>`;
  }
  if (source === "saved") {
    return `<span class="selection-ask-badge selection-ask-badge-saved">已保存</span>`;
  }
  return "";
}

function createSelectionAskController(config: {
  readingContext: ReadingContextSource;
  effectiveLevel: EnglishLevel;
  readingId?: string | null;
  onAnnotationSaved?: () => void;
}) {
  const { readingContext, effectiveLevel, readingId, onAnnotationSaved } = config;
  let askBtn: HTMLButtonElement | null = null;
  let panel: HTMLElement | null = null;
  let streamAbort: AbortController | null = null;

  const hideAskBtn = (): void => {
    askBtn?.remove();
    askBtn = null;
  };

  const closePanel = (): void => {
    streamAbort?.abort();
    streamAbort = null;
    panel?.remove();
    panel = null;
  };

  const cleanup = (): void => {
    hideAskBtn();
    closePanel();
  };

  const showSavedAnswer = (
    selectedText: string,
    answer: string,
    source: PanelSource,
    onReask?: () => void,
  ): void => {
    closePanel();

    panel = document.createElement("aside");
    panel.className = "selection-ask-panel";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", "AI 解答");
    panel.innerHTML = `
      <header class="selection-ask-panel-head">
        <div class="selection-ask-panel-title-row">
          <h3 class="selection-ask-panel-title">AI 解答</h3>
          ${sourceBadge(source)}
        </div>
        <div class="selection-ask-panel-actions">
          ${onReask ? `<button type="button" class="btn btn-sm selection-ask-reask">重新提问</button>` : ""}
          <button type="button" class="btn btn-sm selection-ask-close" aria-label="关闭">✕</button>
        </div>
      </header>
      <blockquote class="selection-ask-quote">${esc(selectedText)}</blockquote>
      <div class="selection-ask-answer markdown" aria-live="polite"></div>
    `;
    document.body.appendChild(panel);

    const answerEl = panel.querySelector<HTMLElement>(".selection-ask-answer");
    const closeBtn = panel.querySelector<HTMLButtonElement>(".selection-ask-close");
    const reaskBtn = panel.querySelector<HTMLButtonElement>(".selection-ask-reask");
    if (!answerEl || !closeBtn) {
      return;
    }

    setMarkdownContent(answerEl, answer);
    closeBtn.addEventListener("click", closePanel);
    reaskBtn?.addEventListener("click", () => {
      closePanel();
      onReask?.();
    });
  };

  const streamAnswer = async (selectedText: string): Promise<void> => {
    closePanel();

    panel = document.createElement("aside");
    panel.className = "selection-ask-panel";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", "AI 解答");
    panel.innerHTML = `
      <header class="selection-ask-panel-head">
        <div class="selection-ask-panel-title-row">
          <h3 class="selection-ask-panel-title">AI 解答</h3>
        </div>
        <div class="selection-ask-panel-actions">
          <button type="button" class="btn btn-sm selection-ask-abort" hidden>中止</button>
          <button type="button" class="btn btn-sm selection-ask-close" aria-label="关闭">✕</button>
        </div>
      </header>
      <blockquote class="selection-ask-quote">${esc(selectedText)}</blockquote>
      <div class="selection-ask-answer markdown" aria-live="polite">
        <div class="selection-ask-loading">
          <span class="streaming-spinner" aria-hidden="true"></span>
          <span>正在思考…</span>
        </div>
      </div>
    `;
    document.body.appendChild(panel);

    const answerEl = panel.querySelector<HTMLElement>(".selection-ask-answer");
    const abortBtn = panel.querySelector<HTMLButtonElement>(".selection-ask-abort");
    const closeBtn = panel.querySelector<HTMLButtonElement>(".selection-ask-close");
    if (!answerEl || !abortBtn || !closeBtn) {
      return;
    }

    closeBtn.addEventListener("click", closePanel);
    abortBtn.addEventListener("click", () => {
      streamAbort?.abort();
    });

    streamAbort = new AbortController();
    const abort = streamAbort;
    abortBtn.hidden = false;

    try {
      const messages = buildSelectionQuestionPrompt(
        selectedText,
        resolveContext(readingContext),
        effectiveLevel,
      );
      const answer = await streamChat(messages, {
        signal: abort.signal,
        onChunk: (aggregated) => {
          setMarkdownContent(answerEl, aggregated);
        },
      });

      if (readingId && answer.trim()) {
        await saveAnnotation({ readingId, selectedText, answer });
        onAnnotationSaved?.();

        const titleRow = panel?.querySelector(".selection-ask-panel-title-row");
        if (titleRow && !titleRow.querySelector(".selection-ask-badge-saved")) {
          titleRow.insertAdjacentHTML("beforeend", sourceBadge("saved"));
        }
        if (!panel?.querySelector(".selection-ask-reask")) {
          const actions = panel?.querySelector(".selection-ask-panel-actions");
          const reask = document.createElement("button");
          reask.type = "button";
          reask.className = "btn btn-sm selection-ask-reask";
          reask.textContent = "重新提问";
          reask.addEventListener("click", () => {
            closePanel();
            void streamAnswer(selectedText);
          });
          actions?.insertBefore(reask, actions.firstChild);
        }
      }
    } catch (err) {
      if (abort.signal.aborted) {
        answerEl.innerHTML = `<p class="muted">已中止</p>`;
        return;
      }
      const message = err instanceof Error ? err.message : "AI 请求失败";
      answerEl.innerHTML = `<p class="muted">${esc(message)}</p>`;
      toast(message);
    } finally {
      abortBtn.hidden = true;
      if (streamAbort === abort) {
        streamAbort = null;
      }
    }
  };

  const openPanel = async (selectedText: string, options: { forceRefresh?: boolean } = {}): Promise<void> => {
    if (readingId && !options.forceRefresh) {
      const cached = await getAnnotationByText(readingId, selectedText);
      if (cached) {
        showSavedAnswer(cached.selectedText, cached.answer, "cache", () => {
          void streamAnswer(selectedText);
        });
        return;
      }
    }
    await streamAnswer(selectedText);
  };

  const openAnnotation = async (annotation: SelectionAnnotation): Promise<void> => {
    showSavedAnswer(annotation.selectedText, annotation.answer, "saved", () => {
      void streamAnswer(annotation.selectedText);
    });
  };

  const openAnnotationById = async (id: string): Promise<void> => {
    const annotation = await getAnnotation(id);
    if (!annotation) {
      toast("划词笔记不存在");
      return;
    }
    await openAnnotation(annotation);
  };

  const showAskBtn = (text: string, rect: DOMRect): void => {
    hideAskBtn();
    askBtn = document.createElement("button");
    askBtn.type = "button";
    askBtn.className = "selection-ask-btn";
    askBtn.textContent = "问 AI";
    askBtn.setAttribute("aria-label", "对选中文本问 AI");
    document.body.appendChild(askBtn);
    positionFloating(askBtn, rect);

    askBtn.addEventListener("mousedown", (event) => {
      event.preventDefault();
      event.stopPropagation();
    });

    askBtn.addEventListener("click", (event) => {
      event.stopPropagation();
      const selected = text;
      hideAskBtn();
      window.getSelection()?.removeAllRanges();
      void openPanel(selected);
    });
  };

  return {
    showAskBtn,
    hideAskBtn,
    closePanel,
    cleanup,
    openAnnotationById,
    panelRef: () => panel,
    askBtnRef: () => askBtn,
  };
}

export type SelectionAskHandle = {
  detach: () => void;
  openAnnotationById: (id: string) => Promise<void>;
};

export function attachSelectionAsk(options: AttachSelectionAskOptions): SelectionAskHandle {
  const { root, readingContext, effectiveLevel, readingId, onAnnotationSaved } = options;
  const allowed = options.allowedSelectors ?? DEFAULT_ALLOWED;
  const ctrl = createSelectionAskController({
    readingContext,
    effectiveLevel,
    readingId,
    onAnnotationSaved,
  });

  const onMouseUp = (): void => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    const text = selection.toString().trim();
    if (!text || isWhitespaceOnly(text)) {
      ctrl.hideAskBtn();
      return;
    }

    if (!root.contains(range.commonAncestorContainer)) {
      ctrl.hideAskBtn();
      return;
    }

    if (!selectionInAllowed(range.commonAncestorContainer, root, allowed)) {
      ctrl.hideAskBtn();
      return;
    }

    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      ctrl.hideAskBtn();
      return;
    }

    ctrl.showAskBtn(text, rect);
  };

  const onMouseDown = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (ctrl.askBtnRef()?.contains(target)) {
      return;
    }
    if (ctrl.panelRef()?.contains(target)) {
      return;
    }
    const mark = (event.target as Element).closest?.(".annotation-mark");
    if (mark) {
      return;
    }
    ctrl.hideAskBtn();
  };

  const onClick = (event: MouseEvent): void => {
    const mark = (event.target as Element).closest?.(".annotation-mark");
    if (!mark || !root.contains(mark)) {
      return;
    }
    const id = mark.getAttribute("data-annotation-id");
    if (!id) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    window.getSelection()?.removeAllRanges();
    ctrl.hideAskBtn();
    void ctrl.openAnnotationById(id);
  };

  const onKeyUp = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      if (ctrl.panelRef()) {
        ctrl.closePanel();
      } else {
        ctrl.hideAskBtn();
      }
    }
  };

  root.addEventListener("mouseup", onMouseUp);
  root.addEventListener("click", onClick);
  document.addEventListener("mousedown", onMouseDown);
  document.addEventListener("keyup", onKeyUp);

  return {
    detach: () => {
      root.removeEventListener("mouseup", onMouseUp);
      root.removeEventListener("click", onClick);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keyup", onKeyUp);
      ctrl.cleanup();
    },
    openAnnotationById: (id) => ctrl.openAnnotationById(id),
  };
}

export type AttachTextareaSelectionAskOptions = {
  textarea: HTMLTextAreaElement;
  getReadingContext: () => string;
  effectiveLevel: EnglishLevel;
  readingId?: string | null;
  onAnnotationSaved?: () => void;
};

export function attachTextareaSelectionAsk(
  options: AttachTextareaSelectionAskOptions,
): () => void {
  const { textarea, getReadingContext, effectiveLevel, readingId, onAnnotationSaved } = options;
  const ctrl = createSelectionAskController({
    readingContext: getReadingContext,
    effectiveLevel,
    readingId,
    onAnnotationSaved,
  });

  const updateFromTextarea = (): void => {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    if (start === end) {
      ctrl.hideAskBtn();
      return;
    }

    const text = textarea.value.slice(start, end).trim();
    if (!text || isWhitespaceOnly(text)) {
      ctrl.hideAskBtn();
      return;
    }

    const rect = textarea.getBoundingClientRect();
    const anchor = new DOMRect(
      rect.left + rect.width * 0.5,
      rect.top + 20,
      0,
      0,
    );
    ctrl.showAskBtn(text, anchor);
  };

  const onMouseDown = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (ctrl.askBtnRef()?.contains(target)) {
      return;
    }
    if (ctrl.panelRef()?.contains(target)) {
      return;
    }
    if (target !== textarea) {
      ctrl.hideAskBtn();
    }
  };

  const onKeyUp = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      if (ctrl.panelRef()) {
        ctrl.closePanel();
      } else {
        ctrl.hideAskBtn();
      }
      return;
    }
    if (event.target === textarea) {
      updateFromTextarea();
    }
  };

  textarea.addEventListener("mouseup", updateFromTextarea);
  textarea.addEventListener("keyup", updateFromTextarea);
  document.addEventListener("mousedown", onMouseDown);
  document.addEventListener("keyup", onKeyUp);

  return () => {
    textarea.removeEventListener("mouseup", updateFromTextarea);
    textarea.removeEventListener("keyup", updateFromTextarea);
    document.removeEventListener("mousedown", onMouseDown);
    document.removeEventListener("keyup", onKeyUp);
    ctrl.cleanup();
  };
}

export function renderAnnotatedText(
  text: string,
  annotations: Array<{ id: string; selectedText: string }>,
): string {
  let result = esc(text);
  const sorted = [...annotations].sort((a, b) => {
    const posA = text.indexOf(a.selectedText);
    const posB = text.indexOf(b.selectedText);
    return posB - posA;
  });

  for (const ann of sorted) {
    const needle = esc(ann.selectedText);
    if (!needle) {
      continue;
    }
    const idx = result.indexOf(needle);
    if (idx === -1) {
      continue;
    }
    const before = result.slice(0, idx);
    const match = result.slice(idx, idx + needle.length);
    const after = result.slice(idx + needle.length);
    result =
      before +
      `<mark class="annotation-mark" data-annotation-id="${esc(ann.id)}" title="点击查看 AI 笔记">${match}</mark>` +
      after;
  }

  return result;
}
